import type { UIMessage } from "@ai-sdk/react";
import { getToolName, isToolUIPart } from "ai";
import Markdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { FlotionMark } from "@/components/brand/FlotionLogo";
import { ToolCallCard } from "./ToolCallCard";

interface ChatMessageProps {
  message: UIMessage;
  /** When true, append a blinking caret after the assistant's streamed text. */
  streaming?: boolean;
}

export function ChatMessage({ message, streaming = false }: ChatMessageProps) {
  const { role, parts } = message;
  const isUser = role === "user";

  if (!parts?.length) return null;

  // User — right-aligned dark pill.
  if (isUser) {
    const text = parts
      .filter((p) => p.type === "text")
      .map((p) => (p as { text: string }).text)
      .join("");
    if (!text) return null;
    return (
      <div className="flex justify-end">
        <div className="max-w-[85%] whitespace-pre-wrap rounded-2xl rounded-br-md bg-foreground px-3.5 py-2.5 text-sm leading-relaxed text-background">
          {text}
        </div>
      </div>
    );
  }

  // Atlas — avatar + unfilled text column.
  return (
    <div className="flex items-start gap-2.5">
      <span className="mt-0.5 grid size-7 shrink-0 place-items-center rounded-full bg-primary/15">
        <FlotionMark size={14} />
      </span>
      <div className="min-w-0 flex-1 space-y-2 pt-0.5">
        {parts.map((part) => {
          if (part.type === "text" && part.text) {
            return (
              <div
                key={`text-${part.text.slice(0, 24)}`}
                className="chat-markdown max-w-none text-sm leading-relaxed text-foreground"
              >
                <Markdown remarkPlugins={[remarkGfm]}>{part.text}</Markdown>
              </div>
            );
          }
          if (isToolUIPart(part)) {
            return (
              <ToolCallCard
                key={`tool-${part.toolCallId}`}
                toolName={getToolName(part)}
                state={part.state}
              />
            );
          }
          return null;
        })}
        {streaming && (
          <span
            className="inline-block h-[1em] w-0.5 translate-y-[2px] animate-pulse bg-primary align-middle"
            aria-hidden="true"
          />
        )}
      </div>
    </div>
  );
}
